import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  type AlertRecord,
  useGetPinnedAlertsForCurrentUserQuery,
  usePinAlertMutation,
  useUnpinAlertMutation,
} from '~/api/generated-api';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';
import { useAlertsPagination } from '../hooks/useAlertsPagination';

interface PinnedAlertsContextType {
  pinnedAlerts: AlertRecord[];
  addPinnedAlert: (alert: AlertRecord) => void;
  removePinnedAlert: (alert: AlertRecord) => void;
  isPinned: (alert: AlertRecord) => boolean;
  loadMorePinnedAlerts: () => void;
  isPinnedLoading: boolean;
  isPinnedFetching: boolean;
  isPinnedAlertsModified: boolean;
  showLessPinned: boolean;
  hasMorePinnedAlerts: boolean;
  setShowLessPinned: (showLess: boolean) => void;
}

const PinnedAlertsContext = createContext<PinnedAlertsContextType | undefined>(
  undefined
);

interface PinnedAlertsProviderProps {
  children: React.ReactNode;
}

export const PinnedAlertsProvider = ({
  children,
}: PinnedAlertsProviderProps) => {
  const orgId = useSelector(selectActingProfileOrganization)?.id;

  /* api for pinning/unpinning of alerts*/
  const [pinAlert] = usePinAlertMutation();
  const [unpinAlert] = useUnpinAlertMutation();

  /* Pinned alerts list */
  const [pinnedToken, setPinnedToken] = useState<string | undefined>(undefined);
  const [isPinnedAlertsModified, setIsPinnedAlertsModified] = useState(false);
  const [showLess, setShowLess] = useState(true);
  const [removedPinnedAlerts, setRemovedPinnedAlerts] = useState<AlertRecord[]>(
    []
  );
  const { data, isLoading, isFetching } = useGetPinnedAlertsForCurrentUserQuery(
    {
      size: 20,
      'cs-continuation-token': pinnedToken,
      'cs-organization': orgId ?? '',
    },
    { refetchOnMountOrArgChange: true }
  );
  const { alerts, hasMoreAlerts, needsNextPage, handleAddNotification } =
    useAlertsPagination({
      data: data,
    });

  const continuationToken = data?.result?.continuation_token;
  useEffect(() => {
    if (needsNextPage && !isFetching && continuationToken) {
      setPinnedToken(continuationToken);
    }
  }, [needsNextPage, isFetching, continuationToken]);

  /* Check if the alert is removed from the pinned alerts list */
  const isRemovedPinnedAlert = useCallback(
    (alert: AlertRecord) => removedPinnedAlerts.some((a) => a.id === alert.id),
    [removedPinnedAlerts]
  );

  /* Hide the show less button if there are no alerts */
  useEffect(() => {
    if (alerts.length === 0 && showLess) setShowLess(false);
  }, [alerts, showLess]);

  /* Final pinned alerts list provided by context; excludes alerts removed by the UI */
  const pinnedAlerts = useMemo(() => {
    const filteredAlerts: AlertRecord[] = alerts.filter(
      (a) => !isRemovedPinnedAlert(a)
    );
    if (showLess) {
      if (filteredAlerts.length === 3) setShowLess(false);
      return filteredAlerts.slice(0, Math.min(filteredAlerts.length, 3));
    }
    return filteredAlerts;
  }, [alerts, isRemovedPinnedAlert, showLess]);

  /* Function to trigger loading more pinned alerts */
  const loadMorePinnedAlerts = () => {
    if (
      !hasMoreAlerts ||
      showLess ||
      isLoading ||
      !data?.result?.continuation_token
    ) {
      return;
    }

    setPinnedToken(data?.result?.continuation_token);
  };

  /* Function to add an alert to the pinned list and call pinning api */
  const addPinnedAlert = useCallback(
    (alert: AlertRecord) => {
      setIsPinnedAlertsModified(true);
      if (alerts.length !== 0 && !alerts.some((a) => a.id === alert.id)) {
        handleAddNotification({ ...alert, pinned: true });
      }
      if (isRemovedPinnedAlert(alert)) {
        setRemovedPinnedAlerts((prev) => prev.filter((a) => a.id !== alert.id));
      }
      if (orgId) {
        pinAlert({
          id: alert.id,
          'cs-organization': orgId,
        }).catch((error) => {
          console.error('Failed to pin alert:', error);
        });
      }
    },
    [pinAlert, handleAddNotification, alerts, isRemovedPinnedAlert, orgId]
  );

  /* Function to remove an alert from the pinned list and call unpinning api */
  const removePinnedAlert = useCallback(
    (alert: AlertRecord) => {
      setIsPinnedAlertsModified(true);
      setRemovedPinnedAlerts((prev) => [...prev, alert]);
      if (orgId) {
        unpinAlert({
          id: alert.id,
          'cs-organization': orgId,
        }).catch((error) => {
          console.error('Failed to unpin alert:', error);
        });
      }
    },
    [unpinAlert, orgId]
  );

  /* Function to check if an alert is pinned; used to determine pin/unpin UI option */
  const isPinned = useCallback(
    (alert: AlertRecord) => pinnedAlerts.some((a) => a.id === alert.id),
    [pinnedAlerts]
  );

  return (
    <PinnedAlertsContext.Provider
      value={{
        pinnedAlerts,
        addPinnedAlert,
        removePinnedAlert,
        isPinned,
        loadMorePinnedAlerts,
        isPinnedAlertsModified,
        isPinnedLoading: isLoading,
        isPinnedFetching: isFetching,
        showLessPinned: showLess,
        setShowLessPinned: setShowLess,
        hasMorePinnedAlerts: hasMoreAlerts,
      }}
    >
      {children}
    </PinnedAlertsContext.Provider>
  );
};

export const usePinnedAlerts = () => {
  const context = useContext(PinnedAlertsContext);
  if (!context) {
    throw new Error(
      'usePinnedAlerts must be used within a PinnedAlertsProvider'
    );
  }
  return context;
};
